import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import * as d3 from 'd3';
import { sankeyLinkHorizontal } from 'd3-sankey';
import { normalizeMargin } from '../shared/PDFCommon.js';
import styles from './sankey-chart-component.module.css';

export const TOOLTIP_OFFSET = 12;

// Place a tooltip above-right of an anchor point, flipping left / below when
// it would cross the viewport edge, then clamping inside it. All values are
// viewport (client) pixels, matching the tooltip's position: fixed.
export const computeTooltipPosition = ({
  anchorX, anchorY, tooltipWidth, tooltipHeight, viewportWidth, viewportHeight,
  offset = TOOLTIP_OFFSET,
}) => {
  let left = anchorX + offset;
  if (left + tooltipWidth > viewportWidth - offset) left = anchorX - offset - tooltipWidth;
  let top = anchorY - offset - tooltipHeight;
  if (top < offset) top = anchorY + offset;
  left = Math.max(offset, Math.min(left, viewportWidth - tooltipWidth - offset));
  top = Math.max(offset, Math.min(top, viewportHeight - tooltipHeight - offset));
  return { left, top };
};

// Utility function to convert SVG to image for PDF
const convertSvgToImage = async (svgElement, width, height) => {
  return new Promise((resolve, reject) => {
    try {
      // Clone the SVG to avoid modifying the original
      const clonedSvg = svgElement.cloneNode(true);

      // Ensure SVG has proper dimensions
      clonedSvg.setAttribute('width', width);
      clonedSvg.setAttribute('height', height);

      // Serialize the SVG
      const svgData = new XMLSerializer().serializeToString(clonedSvg);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);

      // Create an image element
      const img = new Image();
      img.onload = () => {
        // Create a canvas to convert to PNG
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        // Fill white background (PDFs need this)
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, width, height);

        // Draw the image
        ctx.drawImage(img, 0, 0);

        // Convert to data URL
        const imageData = canvas.toDataURL('image/png');
        URL.revokeObjectURL(url);
        resolve(imageData);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load SVG image'));
      };

      img.src = url;
    } catch (error) {
      reject(error);
    }
  });
};

// Function to add Sankey chart to PDF document.
// Returns yPosition unchanged if the chart hasn't been rendered yet
// (ref.current is null when its CollapsiblePanel was never expanded).
// Caller is responsible for telling the user the chart was skipped.
export const handlePDFPrintChart = async (doc, margin, yPosition, ref) => {
  margin = normalizeMargin(margin)

  if (!ref || !ref.current || typeof ref.current.getSvgElement !== 'function') {
    console.warn('handlePDFPrintChart: chart ref is not ready; skipping chart in PDF.');
    return yPosition;
  }

  const svgElement = ref.current.getSvgElement();
  if (!svgElement) {
    console.warn('handlePDFPrintChart: getSvgElement returned null; skipping chart in PDF.');
    return yPosition;
  }
  const actualWidth = svgElement.getBoundingClientRect().width;
  const actualHeight = svgElement.getBoundingClientRect().height;


  const chartWidth = ref.current.getWidth() || actualWidth || 1800;
  const chartHeight = ref.current.getHeight() || actualHeight || 800;

  const imageData = await convertSvgToImage(svgElement, chartWidth, chartHeight);
  // Get page dimensions
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const availableWidth = pageWidth - margin.left - margin.right;
  const availableHeight = pageHeight - yPosition - margin.bottom - 20; // 20 for some padding

  // Calculate scale to fit while maintaining aspect ratio
  const scaleX = availableWidth / (chartWidth); // Convert px to mm
  const scaleY = availableHeight / (chartHeight);
  const scale = Math.min(scaleX, scaleY, 1); // Don't scale up

  const scaledWidth = (chartWidth) * scale;
  const scaledHeight = (chartHeight) * scale;

  // Center the image
  const x = (pageWidth - scaledWidth) / 2;

  // Add title
  // doc.setFontSize(16);
  // yPosition += 20; // space before title
  // doc.text('Sankey Diagram', pageWidth / 2, yPosition, { align: 'center' });
  // yPosition += 20; // space after title

  yPosition += 20; // space before chart
  // Add image
  doc.addImage(imageData, 'PNG', x, yPosition, scaledWidth, scaledHeight);

  yPosition += scaledHeight;
  yPosition += 20; // space after chart

  return yPosition;
};

const LABEL_GAP = 2;

// Given label boxes ({col, value, top, bottom}), keep the largest nodes'
// labels first and skip any label that would overlap one already kept in the
// same column. Returns the Set of kept items.
export const pickNonOverlappingLabels = (items, gap = LABEL_GAP) => {
  const keptByCol = new Map();
  const kept = new Set();
  [...items].sort((a, b) => b.value - a.value).forEach(item => {
    const colKept = keptByCol.get(item.col) || [];
    const overlaps = colKept.some(k => item.top < k.bottom + gap && item.bottom + gap > k.top);
    if (!overlaps) {
      colKept.push(item);
      keptByCol.set(item.col, colKept);
      kept.add(item);
    }
  });
  return kept;
};

// Format currency values for better display
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const sankeyWidth = Math.min(window.innerWidth * 0.95, 3000); //1800
const sankeyHeight = sankeyWidth * 0.45; //800

const SankeyChart = forwardRef((props, ref) => {
  const {
    data,
    width = sankeyWidth,
    height = sankeyHeight,
    nodePadding = 50,
    nodeWidth = 20,
    margin = { top: 40, right: 250, bottom: 40, left: 250 },
    onNodeClick,
    onLinkClick,
    onBackgroundClick,
    colorPalette = ["#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd", "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf"],
    defaultNodeColor = "#69b3a2",
    defaultLinkColor = "#aaa",
    cycleLinkColor = "#FF0000",
    cycleOpacity = 0.9,
    cycleStrokeWidth = 3,
    showNodeLabels = true,
    showNodeValues = false,
    getAnchorLevel = (node) => node.anchorLevel || 0,
    anchorLevelNames = ["Anchor Level 1", "Anchor Level 2", "Anchor Level 3"], // Names for anchor levels, e.g. ["Category", "Merchant"]
    topN = 10, // Show top N nodes per level
    formatNodeTooltip = (node) => node.anchorLevel > 0 ? `${node.name}: $${formatCurrency(node.value)}` : node.name,
    formatLinkTooltip = (link) => `${link.source.name} → ${link.target.name}: $${formatCurrency(link.value)}`,
    formatNodeLabel = (node) => {
      const level = getAnchorLevel(node);
      const maxLevel = Math.max(0, ...data.nodes.map(n => getAnchorLevel(n)));
      return level === maxLevel ? `${node.name}: $${formatCurrency(node.value)}` : node.name;
    },
    formatNodeValue = (node) => node.value,
    formatLinkLabel = (link) => `${link.value}`,
    showLinkLabels = true,
    maxLabelLength = 25,
    wrapLabels = true,
    labelFontSize = 11,
    valueFontSize = 10,
    linkLabelFontSize = 10,
    labelPadding = 12,
    highlightCycles = true,
    showCyclesTooltip = true,
    hideCrowdedLabels = false,
    customSankeyLayout
  } = props;
  const svgRef = useRef(null);



  // Expose method to get image data for PDF
  useImperativeHandle(ref, () => ({
    getImageForPdf: async () => {
      if (!svgRef.current) {
        throw new Error('SVG not rendered');
      }
      return await convertSvgToImage(svgRef.current, width, height);
    },
    getSvgElement: () => svgRef.current,
    getWidth: () => width,
    getHeight: () => height
  }));

  useEffect(() => {
    if (!data || !data.nodes || !data.links || !svgRef.current) return;

    d3.select(svgRef.current).selectAll("*").remove();

    const nodes = data.nodes.map(node => ({ ...node }));
    const nodeMap = {};
    nodes.forEach((node, index) => {
      nodeMap[node.id] = index;
    });

    const links = data.links.map(link => {
      const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
      const targetId = typeof link.target === 'object' ? link.target.id : link.target;

      if (nodeMap[sourceId] === undefined || nodeMap[targetId] === undefined) {
        return null;
      }

      return {
        ...link,
        source: nodeMap[sourceId],
        target: nodeMap[targetId],
        isInCycle: link.isInCycle || false,
        cycleIds: link.cycleIds || []
      };
    }).filter(link => link !== null);

    nodes.forEach((node, i) => {
      if (!node.color) {
        node.color = colorPalette[i % colorPalette.length];
      }
    });

    const graph = customSankeyLayout(
      nodes,
      links,
      width,
      height,
      margin,
      nodeWidth,
      nodePadding,
      getAnchorLevel,
      anchorLevelNames,
      topN,
      colorPalette
    );

    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", [0, 0, width, height])
      .attr("style", "max-width: 100%; height: auto;");

    if (onBackgroundClick) {
      svg.append("rect")
        .attr("width", width)
        .attr("height", height)
        .attr("fill", "transparent")
        .attr("pointer-events", "all")
        .style("cursor", "default")
        .on("click", function (event) {
          if (event.target === this) {
            onBackgroundClick(event);
          }
        });
    }

    const tooltip = d3.select("body").append("div")
      .attr("class", styles.tooltip)
      .style("opacity", 0);

    // Anchor points must be client pixels, not viewBox units: the SVG is
    // scaled to its container, so viewBox coordinates drift from the pointer
    // (and off-screen) whenever the chart renders narrower than `width`.
    const showTooltip = (html, anchorX, anchorY) => {
      tooltip.html(html).style("opacity", 1);
      const box = tooltip.node()?.getBoundingClientRect();
      const { left, top } = computeTooltipPosition({
        anchorX,
        anchorY,
        tooltipWidth: box?.width || 0,
        tooltipHeight: box?.height || 0,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
      });
      tooltip.style("left", `${left}px`).style("top", `${top}px`);
    };

    const defs = svg.append("defs");

    graph.links.forEach((link, i) => {
      const gradientId = `sankey-gradient-${i}`;
      const sourceColor = link.source.color || defaultNodeColor;
      const targetColor = link.target.color || defaultNodeColor;

      const gradient = defs.append("linearGradient")
        .attr("id", gradientId)
        .attr("gradientUnits", "userSpaceOnUse")
        .attr("x1", link.source.x1)
        .attr("x2", link.target.x0);

      gradient.append("stop")
        .attr("offset", "0%")
        .attr("stop-color", sourceColor);

      gradient.append("stop")
        .attr("offset", "100%")
        .attr("stop-color", targetColor);
    });

    const linkGroup = svg.append("g")
      .attr("fill", "none")
      .attr("stroke-opacity", 0.6);

    const link = linkGroup.selectAll("path")
      .data(graph.links)
      .join("path")
      .attr("d", sankeyLinkHorizontal())
      .attr("stroke", (d, i) => d.isInCycle ? cycleLinkColor : `url(#sankey-gradient-${i})`)
      .attr("stroke-width", d => {
        const baseWidth = Math.max(1, d.width);
        return d.isInCycle ? baseWidth + cycleStrokeWidth : baseWidth;
      })
      .attr("stroke-opacity", d => d.isInCycle ? cycleOpacity : 0.6)
      .style("cursor", "pointer")
      .attr("class", d => highlightCycles && d.isInCycle ? "cycle-link" : "")
      .on("mouseover", function (event, d) {
        d3.select(this)
          .attr("stroke-opacity", 0.8)
          .attr("stroke-width", d => {
            const baseWidth = Math.max(1, d.width);
            return d.isInCycle ? baseWidth + cycleStrokeWidth + 2 : baseWidth + 2;
          });

        let tooltipContent = formatLinkTooltip(d);

        if (showCyclesTooltip && d.isInCycle) {
          tooltipContent += `<br><strong style="color:${cycleLinkColor}">Part of cycle</strong>`;

          if (d.cycleIds && d.cycleIds.length) {
            tooltipContent += `<br>Cycle${d.cycleIds.length > 1 ? 's' : ''}: ${d.cycleIds.join(', ')}`;
          }
        }

        if (tooltipContent) {
          showTooltip(tooltipContent, event.clientX, event.clientY);
        }
      })
      .on("mouseout", function (event, d) {
        d3.select(this)
          .attr("stroke-opacity", d.isInCycle ? cycleOpacity : 0.6)
          .attr("stroke-width", d => {
            const baseWidth = Math.max(1, d.width);
            return d.isInCycle ? baseWidth + cycleStrokeWidth : baseWidth;
          });
        tooltip.style("opacity", 0);
      })
      .on("click", function (event, d) {
        if (onLinkClick) {
          onLinkClick({
            source: d.source.id,
            target: d.target.id,
            value: d.value,
            transactionIDs: d.transactionIDs || [],
            isInCycle: d.isInCycle,
            cycleIds: d.cycleIds,
            event: event
          });
        }
      });

    const nodeGroup = svg.append("g")
      .selectAll("g")
      .data(graph.nodes)
      .join("g")
      .attr("class", "node-group")
      .style("cursor", "pointer")
      .on("click", function (event, d) {
        if (onNodeClick) {
          onNodeClick({
            id: d.id,
            name: d.name,
            value: d.transactionValue || d.value,
            color: d.color,
            transactionIDs: d.transactionIDs || [],
            anchorLevel: getAnchorLevel(d),
            event: event
          });
        }
      });

    nodeGroup.append("rect")
      .attr("x", d => d.x0)
      .attr("y", d => d.y0)
      .attr("height", d => Math.max(d.y1 - d.y0, 1))
      .attr("width", d => d.x1 - d.x0)
      .attr("fill", d => d.color || defaultNodeColor)
      .attr("stroke", d => d3.color(d.color || defaultNodeColor).darker(0.5))
      .attr("stroke-width", 1)
      .on("mouseover", function (event, d) {
        d3.select(this)
          .attr("stroke", "#000")
          .attr("stroke-width", 2);

        if (highlightCycles) {
          svg.selectAll("path.cycle-link")
            .filter(link => link.source.id === d.id || link.target.id === d.id)
            .attr("stroke-opacity", 1)
            .attr("stroke-width", link => Math.max(1, link.width + cycleStrokeWidth + 2));
        }

        const tooltipContent = formatNodeTooltip(d);
        if (tooltipContent) {
          // Anchor to the node's rendered box rather than the pointer: this
          // handler also runs from the overlay rect's synthetic dispatch,
          // which carries no clientX/clientY.
          const rect = this.getBoundingClientRect();
          showTooltip(tooltipContent, rect.left + rect.width / 2, rect.top + rect.height / 2);
        }
      })
      .on("mouseout", function (event, d) {
        d3.select(this)
          .attr("stroke", d => d3.color(d.color || defaultNodeColor).darker(0.5))
          .attr("stroke-width", 1);

        if (highlightCycles) {
          svg.selectAll("path.cycle-link")
            .attr("stroke-opacity", cycleOpacity)
            .attr("stroke-width", link => Math.max(1, link.width + cycleStrokeWidth));
        }

        tooltip.style("opacity", 0);
      });

    nodeGroup.append("rect")
      .attr("x", d => d.x0 - 1)
      .attr("y", d => d.y0 - 1)
      .attr("height", d => Math.max(d.y1 - d.y0, 1) + 2)
      .attr("width", d => d.x1 - d.x0 + 2)
      .attr("stroke", "#fff")
      .attr("stroke-width", 0)
      .attr("fill", "none")
      .attr("opacity", 0)
      .attr("pointer-events", "all")
      .attr("class", "node-highlight")
      .on("mouseover", function (event, d) {
        d3.select(this.parentNode).select("rect").dispatch("mouseover");
        d3.select(this)
          .attr("stroke-width", 2)
          .attr("opacity", 0.3);
      })
      .on("mouseout", function () {
        d3.select(this.parentNode).select("rect").dispatch("mouseout");
        d3.select(this)
          .attr("stroke-width", 0)
          .attr("opacity", 0);
      });

    const findSafeLabelPosition = (link) => {
      const path = sankeyLinkHorizontal()(link);
      if (!path) return { x: 0, y: 0 };

      const pathNode = document.createElementNS("http://www.w3.org/2000/svg", "path");
      pathNode.setAttribute("d", path);

      const pathLength = pathNode.getTotalLength();
      const point = pathNode.getPointAtLength(pathLength * 0.4);

      return point;
    };

    if (showLinkLabels) {
      const linkLabelGroup = svg.append("g")
        .attr("class", "link-labels");

      linkLabelGroup.selectAll("rect")
        .data(graph.links)
        .join("rect")
        .attr("x", d => {
          const pos = findSafeLabelPosition(d);
          const labelWidth = formatLinkLabel(d).length * 6;
          return pos.x - labelWidth / 2 - 3;
        })
        .attr("y", d => {
          const pos = findSafeLabelPosition(d);
          return pos.y - 14;
        })
        .attr("width", d => formatLinkLabel(d).length * 6 + 6)
        .attr("height", 18)
        .attr("rx", 3)
        .attr("fill", "transparent")
        .attr("fill-opacity", 0.8)
        .attr("pointer-events", "none")
        .attr("class", d => highlightCycles && d.isInCycle ? "cycle-link-label-bg" : "");

      linkLabelGroup.selectAll("text")
        .data(graph.links)
        .join("text")
        .attr("dy", "0.35em")
        .attr("text-anchor", "middle")
        .attr("font-size", `${linkLabelFontSize}px`)
        .attr("font-weight", "bold")
        .attr("fill", d => highlightCycles && d.isInCycle ? cycleLinkColor : "#333")
        .attr("pointer-events", "none")
        .text(d => formatLinkLabel(d))
        .attr("x", d => findSafeLabelPosition(d).x)
        .attr("y", d => findSafeLabelPosition(d).y)
        .attr("class", d => highlightCycles && d.isInCycle ? "cycle-link-label" : "");
    }

    const wrapText = (selection, maxWidth) => {
      selection.each(function () {
        const text = d3.select(this);
        const words = text.text().split(/\s+/).reverse();
        const lineHeight = 1.1;
        const y = text.attr("y");
        const dy = parseFloat(text.attr("dy")) || 0;

        let tspan = text.text(null)
          .append("tspan")
          .attr("x", text.attr("x"))
          .attr("y", y)
          .attr("dy", dy + "em");

        let line = [];
        let lineNumber = 0;
        let word;

        while (word = words.pop()) {
          line.push(word);
          tspan.text(line.join(" "));

          if (tspan.node().getComputedTextLength() > maxWidth) {
            line.pop();
            tspan.text(line.join(" "));
            line = [word];

            tspan = text.append("tspan")
              .attr("x", text.attr("x"))
              .attr("y", y)
              .attr("dy", ++lineNumber * lineHeight + dy + "em")
              .text(word);
          }
        }
      });
    };

    const addLabelBackground = (selection) => {
      selection.each(function () {
        const text = d3.select(this);
        const bbox = this.getBBox();
        const padding = 2;

        d3.select(this.parentNode).insert("rect", "text")
          .attr("x", bbox.x - padding)
          .attr("y", bbox.y - padding)
          .attr("width", bbox.width + (padding * 2))
          .attr("height", bbox.height + (padding * 2))
          .attr("fill", "transparent")
          .attr("fill-opacity", 0.9)
          .attr("rx", 2)
          .attr("pointer-events", "none");
      });
    };

    if (showNodeLabels) {
      const nodeLabelGroup = svg.append("g")
        .attr("class", "node-labels");

      // Label sidedness is chart-center-based so a multi-column (tiered)
      // layout labels its left half to the left and its right half to the
      // right — inflow tiers read outward-left, outflow tiers outward-right.
      const chartCenterX = width / 2;
      const leftLabels = nodeLabelGroup.selectAll(".left-label")
        .data(graph.nodes.filter(d => (d.x0 + d.x1) / 2 < chartCenterX))
        .join("text")
        .attr("class", "left-label")
        .attr("x", d => d.x0 - labelPadding)
        .attr("y", d => (d.y1 + d.y0) / 2)
        .attr("dy", "0.35em")
        .attr("text-anchor", "end")
        .text(d => formatNodeLabel(d))
        .attr("font-size", `${labelFontSize}px`)
        .attr("font-weight", "bold")
        .attr("fill", "#333")
        .attr("pointer-events", "none");

      if (wrapLabels) {
        leftLabels.call(wrapText, margin.left - labelPadding - 10);
      }

      const rightLabels = nodeLabelGroup.selectAll(".right-label")
        .data(graph.nodes.filter(d => (d.x0 + d.x1) / 2 >= chartCenterX))
        .join("text")
        .attr("class", "right-label")
        .attr("x", d => d.x1 + labelPadding)
        .attr("y", d => (d.y1 + d.y0) / 2)
        .attr("dy", "0.35em")
        .attr("text-anchor", "start")
        .text(d => formatNodeLabel(d))
        .attr("font-size", `${labelFontSize}px`)
        .attr("font-weight", "bold")
        .attr("fill", "#333")
        .attr("pointer-events", "none");

      if (wrapLabels) {
        rightLabels.call(wrapText, margin.right - labelPadding - 10);
      }

      // A crowded column (many thin nodes) would stack labels on top of each
      // other into an unreadable smear. Keep the labels of the largest nodes
      // and drop any that would overlap one already kept; the node's name and
      // amount stay available on hover.
      if (hideCrowdedLabels) {
        const items = [];
        nodeLabelGroup.selectAll("text").each(function (d) {
          if (typeof this.getBBox !== "function") return;
          const box = this.getBBox();
          items.push({ el: this, col: Math.round(d.x0), value: d.value || 0, top: box.y, bottom: box.y + box.height });
        });
        const kept = pickNonOverlappingLabels(items);
        items.forEach(item => { if (!kept.has(item)) item.el.remove(); });
      }

      if (wrapLabels) {
        nodeLabelGroup.selectAll("text").call(addLabelBackground);
      }
    }

    if (showNodeValues) {
      const nodeValueGroup = svg.append("g")
        .attr("class", "node-values");

      nodeValueGroup.selectAll("text")
        .data(graph.nodes.filter(d => (d.y1 - d.y0) > 20))
        .join("text")
        .attr("x", d => (d.x0 + d.x1) / 2)
        .attr("y", d => (d.y1 + d.y0) / 2)
        .attr("dy", "0.35em")
        .attr("text-anchor", "middle")
        .text(d => formatNodeValue(d))
        .attr("font-size", `${valueFontSize}px`)
        .attr("fill", "white")
        .attr("pointer-events", "none");
    }

    return () => {
      tooltip.remove();
    };
  }, [
    data, width, height, nodePadding, nodeWidth, margin,
    onNodeClick, onLinkClick, onBackgroundClick, colorPalette, defaultNodeColor,
    defaultLinkColor, showNodeLabels, showNodeValues, formatNodeTooltip, formatLinkTooltip,
    formatNodeLabel, formatNodeValue, formatLinkLabel, showLinkLabels,
    maxLabelLength, wrapLabels, labelFontSize, valueFontSize, linkLabelFontSize, labelPadding,
    highlightCycles, cycleLinkColor, cycleOpacity, cycleStrokeWidth, showCyclesTooltip, getAnchorLevel,
    hideCrowdedLabels, customSankeyLayout
  ]);

  return (
    <div className="sankey-chart-container">
      <svg ref={svgRef} className="w-full h-full"></svg>
    </div>
  );
});

SankeyChart.displayName = 'SankeyChart';

export default SankeyChart;